import React from "react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#E4E4E7] py-12 px-6">
      <div className="max-w-3xl mx-auto" data-testid="privacy-policy-page">
        <div className="mb-8 flex items-center justify-between">
          <Link to="/" className="text-[#FFEB00] hover:underline text-sm" data-testid="back-to-app-link">
            ← Back to RevFlow
          </Link>
          <span className="text-xs text-[#71717A]">Last updated: 28 April 2026</span>
        </div>

        <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-sm text-[#71717A] mb-10">
          RevFlow is an internal billing &amp; revenue-tracking platform operated by The Digital Impressions
          (the "Operator"). This document describes how we collect, use, and protect data within the platform.
        </p>

        <div className="space-y-8 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-[#FFEB00] mb-2">1. Scope</h2>
            <p>
              RevFlow is a private application used by the Operator&apos;s internal team to manage agency
              billing, commission tracking, and integrations with services such as Zoho Books, Shopify, Meta
              (Facebook) Marketing, Gmail, and CRM systems on behalf of the Operator&apos;s own clients. This
              policy applies to all data flowing through RevFlow.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#FFEB00] mb-2">2. Data We Collect</h2>
            <ul className="list-disc list-inside space-y-1.5 text-[#A1A1AA]">
              <li>OAuth tokens and account identifiers from Zoho Books, Zoho CRM, Shopify, Meta (Facebook), Google.</li>
              <li>Aggregate revenue, order, and ad-spend metrics retrieved via those APIs.</li>
              <li>Contact and company records imported via CSV or CRM sync.</li>
              <li>Operator-team email addresses used for sign-in.</li>
            </ul>
            <p className="mt-2 text-[#A1A1AA]">
              We do not sell any data and we do not share data with third parties beyond the integrations the
              Operator has explicitly connected.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#FFEB00] mb-2">3. How We Use the Data</h2>
            <ul className="list-disc list-inside space-y-1.5 text-[#A1A1AA]">
              <li>To compute monthly billing, commissions, and projections.</li>
              <li>To generate invoices and push them to Zoho Books on behalf of the Operator.</li>
              <li>To surface dashboards, signals, and reminders for the Operator&apos;s internal team.</li>
              <li>To support the Operator&apos;s relationship-management workflows.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#FFEB00] mb-2">4. Meta (Facebook) Data</h2>
            <p>
              When an Operator team-member connects a Meta ad account via Facebook Login, RevFlow requests
              the <span className="text-[#E4E4E7]">ads_read</span> and{" "}
              <span className="text-[#E4E4E7]">business_management</span> permissions. We use these solely to
              fetch monthly ad-spend totals for ROI calculations. RevFlow does not post on the user&apos;s
              behalf, does not access personal Facebook data outside the granted scopes, and does not retain
              any ad-creative or audience information.
            </p>
            <p className="mt-2 text-[#A1A1AA]">
              An Operator team-member may disconnect Meta access at any time from the Settings page or by
              revoking RevFlow access from{" "}
              <a
                href="https://www.facebook.com/settings?tab=business_tools"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#FFEB00] hover:underline"
              >
                Facebook Business Integrations
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#FFEB00] mb-2">5. Data Retention &amp; Deletion</h2>
            <p>
              All data is stored within RevFlow&apos;s private MongoDB database controlled by the Operator. To
              request deletion of any data routed through RevFlow (including disconnecting third-party tokens
              and erasing related records), contact the Operator at{" "}
              <a href="mailto:contact@amansaluja.com" className="text-[#FFEB00] hover:underline">
                contact@amansaluja.com
              </a>
              . We will action verified deletion requests within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#FFEB00] mb-2">6. Security</h2>
            <p>
              OAuth tokens and credentials are stored encrypted at rest and transmitted over TLS. Access is
              restricted to authenticated members of the Operator&apos;s team via email/OTP or Google sign-in.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#FFEB00] mb-2">7. Contact</h2>
            <p>
              For privacy or data questions please email{" "}
              <a href="mailto:contact@amansaluja.com" className="text-[#FFEB00] hover:underline">
                contact@amansaluja.com
              </a>
              .
            </p>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-[#1F1F1F] text-xs text-[#52525B]">
          © {new Date().getFullYear()} The Digital Impressions. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
